<template>
  <b-form class="essay-area p-0" @submit.prevent="handleSubmit">
    <b-container fluid class="essay-area p-0">
      <b-row class="form-row mx-auto pt-4" align-h="center">
        <b-col>
          <b-form-group
            v-for="stream in stream"
            :key="stream.title"
            class="stream-droplet px-4"
          >
            <div
              v-if="stream.title === 'Conclusion'"
              class="add-evidence-button mx-auto user-responsivity"
              @click="addEvidence"
            >+ Add evidence section</div>
            <div class="area-title-row">
              <span class="area-title">{{ stream.title }}</span>
              <transition name="confirm-fade">
                <div
                  v-if="confirmDeleteTitle === stream.title"
                  class="delete-confirm"
                >
                  <span class="delete-confirm-text">Delete this section?</span>
                  <button type="button" class="delete-confirm-yes" @click="confirmDelete(stream.title)">Delete</button>
                  <button type="button" class="delete-confirm-cancel" @click="confirmDeleteTitle = null">Cancel</button>
                </div>
              </transition>
              <button
                v-if="stream.title && stream.title.startsWith('Evidence') && canRemoveEvidence && confirmDeleteTitle !== stream.title"
                class="remove-evidence-btn"
                type="button"
                @click="confirmDeleteTitle = stream.title"
                title="Delete section"
                aria-label="Delete section"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z"/>
                  <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z"/>
                </svg>
              </button>
            </div>
            <br />
            <div class="area-subtitle">{{ stream.subtitle }}</div>
            <textareavue
              @focusin="handleFocus"
              :moduleVersion="moduleVersion"
              :dataRequested="dataRequested"
              :string="stream.template.template"
              :bgColor="stream.palette"
              @click.native="setCurrentSection(stream.title)"
              @fulfillRequest="data => handleRequest(data, stream.template)"
            ></textareavue>
            <div v-if="stream.subTemplateSubtitle" class="subsubtitle">
              <div class="area-subtitle">
                {{ stream.subTemplateSubtitle.subtitle }}
              </div>
              <textareavue
                :moduleVersion="moduleVersion"
                :dataRequested="dataRequested"
                :string="stream.subTemplate.template"
                :placeholder="stream.placeholder"
                :bgColor="'#F5E07B'"
                @click.native="
                  setCurrentSection(stream.subTemplateSubtitle.title)
                "
                @fulfillRequest="
                  data => handleRequest(data, stream.subTemplate)
                "
              ></textareavue>
            </div>
          </b-form-group>
        </b-col>
        <b-col class="component p-0 mx-4 mb-5">
          <breakdown
            @changeSection="section => setCurrentSection(section)"
            :currentSection="currentSection"
          ></breakdown>
        </b-col>
      </b-row>
      <b-row class="essay-action-bar px-4 py-3" align-h="end" align-v="center">
        <b-col cols="12" class="submit-actions-shell">
          <b-alert v-if="authPrompt" show variant="warning" class="mb-3 text-start">
            <div>{{ authPrompt }}</div>
            <div v-if="!isAuthenticated" class="mt-2 d-flex gap-2 flex-wrap">
              <b-button size="sm" variant="outline-primary" @click="goToLogin">
                Log in
              </b-button>
              <b-button size="sm" variant="primary" @click="goToSignup">
                Sign up
              </b-button>
            </div>
            <div v-else class="mt-2 d-flex gap-2 flex-wrap">
              <b-button size="sm" variant="primary" @click="goToAccount">
                My Account
              </b-button>
            </div>
          </b-alert>
          <b-alert v-if="submissionError" show variant="danger" class="mb-3 text-start">
            {{ submissionError }}
          </b-alert>
          <div class="submit-controls">
            <b-button
              class="finish-button border-0 fontist purple-button action-btn"
              type="submit"
              :disabled="isSubmitting"
            >{{ isSubmitting ? "Generating your feedback..." : "Submit Essay" }}</b-button>
            <div v-if="isSubmitting" class="submit-note mt-2">
              Submitting again is disabled while feedback is being generated.
            </div>
          </div>
          <feedback-attempts-banner
            class="mt-2 attempts-banner-under-submit"
            :remaining-attempts="remainingFeedbackAttempts"
            :is-authenticated="isAuthenticated"
            :is-pro-user="isProUser"
          />
        </b-col>
      </b-row>
      <feedback-generation-modal
        :visible="isSubmitting"
        :loading-step-message="loadingStepMessage"
        :show-long-wait-message="showLongWaitMessage"
      />

    </b-container>
  </b-form>
</template>

<script>
import { onMounted, onBeforeUnmount, ref, watch, computed, reactive } from "vue";
import { useRoute } from "vue-router";
import breakdown from "./BreakDown.vue";
import storeWriting from "../../../store/writing";
import textareavue from "./TextArea.vue";
import feedbackGenerationModal from "../FeedbackGenerationModal.vue";
import feedbackAttemptsBanner from "../FeedbackAttemptsBanner.vue";
import {
  getFeedback,
  isAuthRequiredError,
  isFeedbackLimitError
} from "@/services/getFeedback";
import { useSubscriptionAccess } from "@/composables/useSubscriptionAccess";
import { logDebugEvent } from "@/services/debugLogs";
import { pushRoute } from "@/router/navigation";
import {
  createEssaySubmission,
  completeEssaySubmission,
  failEssaySubmission
} from "@/services/essaySubmissions";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/firebase";
import { useAuthState } from "@/services/authService";
export default {
  props: ["props", "identification", "moduleVersion"],
  components: {
    breakdown,
    textareavue,
    feedbackGenerationModal,
    feedbackAttemptsBanner
  },
  emits: ["updateProgress"],
  setup(props, { emit }) {
    //STORE
    const isSubmitting = ref(false);
    const authPrompt = ref("");
    const submissionError = ref("");
    const store = storeWriting;
    const route = useRoute();
    const {
      isAuthenticated,
      isProUser,
      normalizedProfile,
      feedbackAccessState,
      refreshSubscriptionProfile
    } = useSubscriptionAccess();

    const remainingFeedbackAttempts = computed(() => {
      if (isProUser.value) {
        return null;
      }

      return normalizedProfile.value.remainingFreeFeedback;
    });

    const loadingStepMessages = [
      "Analyzing your thesis...",
      "Reviewing your evidence...",
      "Evaluating your reasoning...",
      "Putting together feedback..."
    ];
    const loadingStepIndex = ref(0);
    const showLongWaitMessage = ref(false);
    const loadingStepMessage = computed(
      () => loadingStepMessages[loadingStepIndex.value]
    );

    let loadingStepIntervalId = null;
    let longWaitTimeoutId = null;

    const stopSubmittingUi = () => {
      isSubmitting.value = false;
      showLongWaitMessage.value = false;

      if (loadingStepIntervalId) {
        clearInterval(loadingStepIntervalId);
        loadingStepIntervalId = null;
      }

      if (longWaitTimeoutId) {
        clearTimeout(longWaitTimeoutId);
        longWaitTimeoutId = null;
      }
    };

    const startSubmittingUi = () => {
      stopSubmittingUi();
      submissionError.value = "";
      isSubmitting.value = true;
      loadingStepIndex.value = 0;

      loadingStepIntervalId = setInterval(() => {
        loadingStepIndex.value =
          (loadingStepIndex.value + 1) % loadingStepMessages.length;
      }, 3500);

      longWaitTimeoutId = setTimeout(() => {
        showLongWaitMessage.value = true;
      }, 25000);
    };

    const goToLogin = () => {
      pushRoute("Login", {}, { redirect: route.fullPath });
    };

    const goToSignup = () => {
      pushRoute("Signup", {}, { redirect: route.fullPath });
    };

    const goToAccount = () => {
      pushRoute("Account");
    };



    //V-MODEL REFS
    const contextualization = reactive({
      template: props.props.template.templates.contextualization
    });
    const thesis = reactive({
      template: props.props.template.templates.thesis
    });
    const evidence = [];
    props.props.template.templates.evidence.forEach(x => {
      evidence.push([
        reactive({ template: x[0] }),
        reactive({ template: x[1] })
      ]);
    });
    const conclusion = reactive({
      template: props.props.template.templates.conclusion
    });

    //STRUCTURE AVAILABLE TO ADD ADDITIONAL EVIDENCES
    const evidences = ref(evidence.length);
    const canRemoveEvidence = computed(() => evidences.value > 2);

    const confirmDeleteTitle = ref(null);

    const confirmDelete = (title) => {
      confirmDeleteTitle.value = null;
      removeEvidence(title);
    };

    const removeEvidence = (title) => {
      const idx = stream.value.findIndex(s => s.title === title);
      if (idx === -1) return;
      stream.value.splice(idx, 1);
      evidences.value--;
      // Re-number remaining evidence sections
      let count = 1;
      stream.value.forEach(s => {
        if (s.title && s.title.startsWith('Evidence')) {
          s.title = `Evidence #${count}`;
          count++;
        }
      });
    };

    const addEvidence = () => {
      evidences.value++;
      evidence.push([
        reactive({
          template: props.props.template.templates.evidence[0][0]
        }),
        reactive({
          template: props.props.template.templates.evidence[0][1]
        })
      ]);
      const cap = stream.value.pop();
      stream.value.push({
        title: `Evidence #${evidences.value}`,
        subtitle: "fill in the topic sentence template",
        template: evidence[evidence.length - 1][0],
        subTemplate: evidence[evidence.length - 1][1],
        subTemplateSubtitle: evidenceSubTemplateSubtitle,
        palette: "#D289E5"
      });
      stream.value.push(cap);
      console.log("add evidence triggered");
    };
    const initializeEvidences = () => {
      if (evidence.length > 2) {
        for (var i = 2; i < evidence.length; i++) {
          const cap = stream.value.pop();
          stream.value.push({
            title: `Evidence #${i + 1}`,
            subtitle: "fill in the topic sentence template",
            template: evidence[i][0],
            subTemplate: evidence[i][1],
            subTemplateSubtitle: evidenceSubTemplateSubtitle,
            palette: "#D289E5"
          });
          stream.value.push(cap);
        }
      }
    };

    //CONTENT CONSTANTS
    const evidenceSubTemplateSubtitle = {
      subtitle:
        "add 1 sentence to analysis your evidence and support your thesis statement",
      title: "Analysis"
    };
    const stream = ref([
      {
        title: "Contextualization",
        subtitle: "add 1 sentence of historical context relevant to the prompt",
        template: contextualization,
        palette: "#CCEBA5"
      },
      {
        title: "Thesis",
        subtitle: "fill in the Change & Continuity thesis statement template",
        template: thesis,
        palette: "#85CDF9"
      },
      {
        title: "Evidence #1",
        subtitle: "fill in the topic sentence template",
        template: evidence[0][0],
        subTemplate: evidence[0][1],
        subTemplateSubtitle: evidenceSubTemplateSubtitle,
        palette: "#D289E5",
        placeholder: "The result of..."
      },
      {
        title: "Evidence #2",
        subtitle: "fill in the topic sentence template",
        template: evidence[1][0],
        subTemplate: evidence[1][1],
        subTemplateSubtitle: evidenceSubTemplateSubtitle,
        palette: "#D289E5"
      },
      {
        title: "Conclusion",
        subtitle: "add 1 sentence of historical context relevant to the prompt",
        template: conclusion,
        palette: "#FE9A22"
      }
    ]);

    // total emits = stream length and # of evidences (because analysis is wrapped within the evidence obj, but has their own textarea component within their own emits)
    const totalEmits = computed(() => stream.value.length + evidences.value);
    const currentEmits = ref(0);
    const dataRequested = ref(false);

    //Submits user input to store when all textarea components have emitted their events and updated data in parent

    watch(
      currentEmits,
      async (newEmit, oldEmit) => {
        console.log(`${newEmit} emits from ${oldEmit}`);
        if (newEmit === totalEmits.value) {
          if (isSubmitting.value) {
            return;
          }

          console.log("All emits processed, triggering event!");
          let evidencePost = [];
          evidence.forEach(x => {
            evidencePost.push([x[0].template, x[1].template]);
          });

          const hasEssayContent =
            Boolean(String(contextualization.template || "").trim()) ||
            Boolean(String(thesis.template || "").trim()) ||
            Boolean(String(conclusion.template || "").trim()) ||
            evidencePost.some(pair =>
              Array.isArray(pair)
                ? pair.some(item => Boolean(String(item || "").trim()))
                : Boolean(String(pair || "").trim())
            );

          if (!hasEssayContent) {
            submissionError.value =
              "Please write your essay before submitting for feedback.";
            return;
          }
          const idea = {
            prompt_id: props.identification, //ID to identify prompt
            id: store.getters.getUniqueId, //Unique ID assigned to new feedback obj (to be replaced by firestore id's)
            moduleVersion: props.moduleVersion, //For segregating beginner and advanced modules
            promptText:
              props.props && props.props.prompt ? props.props.prompt.prompt : "",

            //User input -- analysis is wrapped within the evidence obj
            contextualization: contextualization.template,
            evidence: evidencePost,
            conclusion: conclusion.template,
            thesis: thesis.template
          };

          // console.log('criminal', idea)
          store.dispatch("setUserInput", idea);
          store.dispatch("setModuleVersion", props.moduleVersion);

          const normalizedEvidencePairs = evidencePost.map(pair => {
            const evidenceText = pair?.[0] || "";
            const analysisText = pair?.[1] || "";
            // If analysis is left blank but evidence has content, fall back so backend can still evaluate reasoning.
            return [
              evidenceText,
              String(analysisText).trim().length ? analysisText : evidenceText
            ];
          });

          const payload = {
            prompt: {
              id: props.identification,
              text:
                props.props && props.props.prompt ? props.props.prompt.prompt : "",
              moduleVersion: props.moduleVersion
            },
            writing: {
              contextualization: contextualization.template,
              thesis: thesis.template,
              evidence: normalizedEvidencePairs,
              evidencePairs: normalizedEvidencePairs,
              ...Object.fromEntries(
                normalizedEvidencePairs.flatMap((pair, index) => [
                  [`evidence${index + 1}`, pair?.[0] || ""],
                  [`analysis${index + 1}`, pair?.[1] || ""]
                ])
              ),
              conclusion: conclusion.template
            }
          };

          if (process.env.NODE_ENV !== "production") {
            const writing = payload.writing || {};
            const evidencePairs = Array.isArray(writing.evidencePairs)
              ? writing.evidencePairs
              : [];

            console.group("[Essay Submit Debug]");
            console.log("moduleVersion", props.moduleVersion);
            console.log("promptId", props.identification);
            console.log("contextualization", writing.contextualization || "");
            console.log("thesis", writing.thesis || "");
            evidencePairs.forEach((pair, index) => {
              console.log(`evidence${index + 1}`, pair?.[0] || "");
              console.log(`analysis${index + 1}`, pair?.[1] || "");
            });
            console.log("conclusion", writing.conclusion || "");
            console.groupEnd();
          }

          let submissionId = null;
          try {
            startSubmittingUi();
            logDebugEvent("essay-area", "submit-attempt", {
              moduleVersion: props.moduleVersion,
              promptId: props.identification,
              isAuthenticated: isAuthenticated.value
            });

            // Save essay BEFORE calling feedback API so work is never lost
            try {
              submissionId = await createEssaySubmission({
                promptId: props.identification,
                promptText: props.props && props.props.prompt ? props.props.prompt.prompt : null,
                essayText: JSON.stringify(idea),
                moduleVersion: props.moduleVersion
              });
            } catch (saveErr) {
              console.warn("Could not pre-save essay submission:", saveErr);
            }

            if (props.moduleVersion === "Beginner") {
              const writing = payload.writing || {};
              const pairs = Array.isArray(writing.evidencePairs)
                ? writing.evidencePairs
                : [];

              logDebugEvent("essay-area", "beginner-payload-debug", {
                promptId: props.identification,
                contextualizationLength: String(writing.contextualization || "").trim().length,
                thesisLength: String(writing.thesis || "").trim().length,
                conclusionLength: String(writing.conclusion || "").trim().length,
                contextualizationPreview: String(writing.contextualization || "").slice(0, 140),
                thesisPreview: String(writing.thesis || "").slice(0, 140),
                conclusionPreview: String(writing.conclusion || "").slice(0, 140),
                pairCount: pairs.length,
                evidencePairs: pairs.map((pair, index) => ({
                  index: index + 1,
                  evidenceLength: String(pair?.[0] || "").trim().length,
                  analysisLength: String(pair?.[1] || "").trim().length,
                  evidencePreview: String(pair?.[0] || "").slice(0, 120),
                  analysisPreview: String(pair?.[1] || "").slice(0, 120)
                }))
              });
            }

            console.log("Submitting payload:", payload);
            let result = null;
            try {
              result = await getFeedback(payload);
              console.log("FUNCTION RESULT:", result);
            } catch (err) {
              console.error("getFeedback ERROR:", err);
              throw err;
            }

            // Debug: read feedbackCount directly from Firestore right after the cloud function to
            // confirm whether the write landed in the database the frontend is connected to.
            try {
              const { user: authUser } = useAuthState();
              if (authUser.value?.uid) {
                const snap = await getDoc(doc(db, "users", authUser.value.uid));
                const directCount = snap.exists() ? snap.data().feedbackCount : "doc-missing";
                console.log("[feedbackCount debug] Direct Firestore read after cloud fn:", directCount);
                logDebugEvent("essay-area", "firestore-direct-read-after-fn", {
                  feedbackCount: directCount,
                  accessReportedCount: result?.access?.feedbackCount ?? null
                });
              }
            } catch (debugErr) {
              console.warn("[feedbackCount debug] Could not read Firestore directly:", debugErr);
            }

            logDebugEvent("essay-area", "submit-result-access", {
              hasAccess: Boolean(result?.access),
              accessFeedbackCount:
                typeof result?.access?.feedbackCount === "number"
                  ? result.access.feedbackCount
                  : null,
              accessFreeFeedbackLimit:
                typeof result?.access?.freeFeedbackLimit === "number"
                  ? result.access.freeFeedbackLimit
                  : null,
              accessRemainingFreeFeedback:
                typeof result?.access?.remainingFreeFeedback === "number"
                  ? result.access.remainingFreeFeedback
                  : null
            });



            if (props.moduleVersion === "Beginner") {
              const sections = result && result.sections ? result.sections : {};
              const summarizeSection = key => {
                const section = sections[key] || {};
                return {
                  key,
                  score: section.score ?? null,
                  maxScore: section.maxScore ?? null,
                  whatWasWrittenLength: String(section.whatWasWritten || "").trim().length,
                  whatWasWrittenPreview: String(section.whatWasWritten || "").slice(0, 120),
                  whatWasGoodPreview: String(section.whatWasGood || "").slice(0, 120),
                  whatToImprovePreview: String(section.whatToImprove || "").slice(0, 120)
                };
              };

              const dynamicEvidenceKeys = Object.keys(sections)
                .filter(key => /^evidence\d+$/.test(key) || /^analysis\d+$/.test(key))
                .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

              logDebugEvent("essay-area", "beginner-response-debug", {
                promptId: props.identification,
                overallScore: result?.overallScore ?? null,
                sections: [
                  summarizeSection("contextualization"),
                  summarizeSection("thesis"),
                  ...dynamicEvidenceKeys.map(summarizeSection),
                  summarizeSection("conclusion")
                ]
              });
            }

            store.dispatch("setBackendFeedback", {
              prompt_id: props.identification,
              moduleVersion: props.moduleVersion,
              feedback: result
            });

            // Mark submission complete in Firestore
            if (submissionId) {
              try {
                await completeEssaySubmission(submissionId, {
                  feedback: result,
                  score: result?.overallScore ?? result?.score ?? null
                });
              } catch (updateErr) {
                console.warn("Could not mark submission complete:", updateErr);
              }
            }

            authPrompt.value = "";
            submissionError.value = "";
            const refreshedProfile = await refreshSubscriptionProfile();
            logDebugEvent("essay-area", "profile-refresh-after-submit", {
              uid: refreshedProfile?.uid || null,
              feedbackCount:
                typeof refreshedProfile?.feedbackCount === "number"
                  ? refreshedProfile.feedbackCount
                  : null,
              freeFeedbackLimit:
                typeof refreshedProfile?.freeFeedbackLimit === "number"
                  ? refreshedProfile.freeFeedbackLimit
                  : null
            });
            logDebugEvent("essay-area", "submit-success", {
              moduleVersion: props.moduleVersion,
              promptId: props.identification,
              overallScore: result?.overallScore ?? null
            });
            console.log("handled submit", store.state.feedback, store.state.completedPrompts);
            pushRoute(
              "004",
              { id: store.state.uniqueId },
              { module: props.moduleVersion }
            );
          } catch (error) {
            console.error("Failed to fetch backend feedback", error);
            logDebugEvent("essay-area", "submit-failed", {
              code: error?.code || null,
              message: error?.message || null,
              isAuthenticated: isAuthenticated.value
            });

            // Mark submission as errored in Firestore
            if (submissionId) {
              try {
                await failEssaySubmission(submissionId, error?.message || "Feedback generation failed");
              } catch (updateErr) {
                console.warn("Could not mark submission as error:", updateErr);
              }
            }

            if (isAuthRequiredError(error)) {
              authPrompt.value =
                "Create a free account to get personalized essay feedback. Your essays, feedback history, and progress will be saved automatically.";
              submissionError.value = "";
            } else if (isFeedbackLimitError(error)) {
              await refreshSubscriptionProfile();
              authPrompt.value =
                "No feedback attempts remaining. Upgrade to continue getting feedback.";
              submissionError.value = "";
            } else {
              authPrompt.value = "";
              submissionError.value =
                "Something went wrong while generating feedback. Please try again. Your essay is still here, and you can resubmit.";
            }
          } finally {
            stopSubmittingUi();
          }
        }
      },
      { deep: true }
    );
    //EVENT HANDLER: SUBMIT
    const handleSubmit = () => {
      if (isSubmitting.value) {
        return;
      }

      //note: check if feedback obj has aleady been created for this prompt before uploading data
      //update: handled already created exception within store
      console.log("submited essay");
      if (!isAuthenticated.value) {
        authPrompt.value =
          "Log in or sign up to continue. Create a free account to get personalized essay feedback.";
        submissionError.value = "";
        return;
      }

      if (!feedbackAccessState.value.allowed) {
        authPrompt.value =
          "No feedback attempts remaining. Upgrade to continue getting feedback.";
        submissionError.value = "";
        return;
      }

      authPrompt.value = "";
      submissionError.value = "";
      currentEmits.value = 0;
      console.log(
        "before submit",
        contextualization.template,
        thesis.template,
        evidence,
        conclusion.template
      );
      // Toggle to guarantee child watchers fire on every submit attempt.
      dataRequested.value = !dataRequested.value;
    };
    const handleRequest = (data, stream) => {
      const normalizedData =
        data && typeof data === "object" && "value" in data ? data.value : data;

      console.log("stream", stream);
      // Beginner mode emits an array of word objects; assemble into a plain string
      if (Array.isArray(normalizedData)) {
        stream.template = normalizedData
          .map(word => {
            const c = word.content;
            // content may still be a Vue ref if the blank was never touched
            return typeof c === "object" && c !== null && "value" in c
              ? c.value
              : c || "";
          })
          .join("");
      } else {
        stream.template = normalizedData;
      }

      console.log("handled", normalizedData, stream, stream.template);
      currentEmits.value++;
    };
    //EVENT HANDLER: BREAKDOWN COMPONENT CONNECTION
    const currentSection = ref(null);
    const setCurrentSection = breakdown => {
      if (breakdown) {
        currentSection.value = breakdown.split(" ")[0];
      } else {
        currentSection.value = null;
      }
      console.log("click", currentSection.value);
      emit("updateProgress", currentSection.value);
    };
    const handleFocus = () => {
      console.log("focused!");
    };
    //LIFECYCLE HOOKS (initialize or reset variables)
    onMounted(() => {
      initializeEvidences();
    });

    onBeforeUnmount(() => {
      stopSubmittingUi();
    });

    // const testing = 'During the time period between ___(date)__ and __(date)___ in __(geographic location)__  there were significant changes in  __(AP Theme)__   such as  __(category)__,  __(category)__ , and __(category)__.'
    return {
      stream,
      evidence,
      evidenceSubTemplateSubtitle,
      currentSection,
      setCurrentSection,
      handleSubmit,
      isSubmitting,
      loadingStepMessage,
      showLongWaitMessage,
      authPrompt,
      submissionError,
      remainingFeedbackAttempts,
      isAuthenticated,
      isProUser,
      goToLogin,
      goToSignup,
      goToAccount,
      handleRequest,
      dataRequested,
      handleFocus,
      addEvidence,
      removeEvidence,
      canRemoveEvidence,
      confirmDeleteTitle,
      confirmDelete
    };
  }
};
</script>

<style scoped>
.essay-area {
  position: relative;
  border-radius: 10px;
  background: #fff;
}

.essay-action-bar {
  border-top: 1px solid #ebebeb;
}

.submit-actions-shell {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
}

.submit-controls {
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
.component {
  max-width: 360px;
}

.form-row {
  max-width: 1080px;
}

.stream-droplet {
  padding-bottom: 42px;
}

.area-title {
  color: var(--text-normal, #18191f);
  font-feature-settings: "liga" off;
  font-size: 21px;
  font-style: normal;
  font-weight: 700;
  line-height: 1.2;
}

.area-subtitle {
  color: var(--text-normal-2, #474a57);
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 1.35;
  padding: 2px 0 8px 0;
}

.subsubtitle {
  padding-top: 5px;
}

.action-btn {
  min-width: 184px;
}

.area-title-row {
  display: flex;
  align-items: center;
  gap: 0;
}

.remove-evidence-btn {
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #b0b4be;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  padding: 0;
  transition: color 0.15s, background 0.15s;
  flex-shrink: 0;
  &:hover {
    color: #c0392b;
    background: #fef2f2;
  }
  svg {
    display: block;
  }
}

.delete-confirm {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.delete-confirm-text {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
  white-space: nowrap;
}

.delete-confirm-yes {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: #e53e3e;
  border: none;
  border-radius: 6px;
  padding: 4px 12px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
  &:hover {
    background: #c0392b;
  }
}

.delete-confirm-cancel {
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 4px 12px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;
  &:hover {
    background: #f7f8fa;
    color: #374151;
  }
}

.confirm-fade-enter-active,
.confirm-fade-leave-active {
  transition: opacity 0.15s;
}
.confirm-fade-enter,
.confirm-fade-leave-to {
  opacity: 0;
}

.add-evidence-button {
  color: var(--brand-purple, #8c30f5);
  font-feature-settings: "liga" off;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 30px;
  margin-bottom: 50px;
  cursor: pointer;
  width: fit-content;
}

.submit-note {
  font-size: 0.85rem;
  color: #6b7280;
  max-width: 260px;
}

.attempts-banner-under-submit {
  width: 600px;
}

</style>
<style lang="sass" scoped src="@/assets/css/essayWriting.sass"></style>
